import { isDecorationWidgetMutation } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { mergeAttributes, type NodeViewRendererProps } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { ViewMutationRecord } from '@tiptap/pm/view';

// function applyColwidth(dom: HTMLElement, colwidth: number[] | null) {
//     if (!colwidth?.length) {
//         return;
//     }
//     dom.setAttribute('colwidth', colwidth.join(','));
// }

function applyTableCellDomAttributes(
    dom: HTMLElement,
    node: PMNode,
    HTMLAttributes: Record<string, unknown>,
    optionsHTMLAttributes: Record<string, unknown>,
) {
    const attrs = mergeAttributes(optionsHTMLAttributes, HTMLAttributes) as Record<string, string>;

    Object.entries(attrs).forEach(([key, value]) => {
        if (value == null || value === '') {
            return;
        }
        dom.setAttribute(key, String(value));
    });

    if (node.attrs.colspan != null && node.attrs.colspan !== 1) {
        dom.setAttribute('colspan', String(node.attrs.colspan));
    }
    if (node.attrs.rowspan != null && node.attrs.rowspan !== 1) {
        dom.setAttribute('rowspan', String(node.attrs.rowspan));
    }

    // applyColwidth(dom, node.attrs.colwidth as number[] | null);
}

function tableCellIgnoreMutation(cellElement: HTMLElement) {
    return (mutation: ViewMutationRecord): boolean => {
        if (mutation.type === 'selection') {
            return false;
        }

        if (!(mutation.target instanceof HTMLElement)) {
            return false;
        }

        const target = mutation.target;

        if (target.closest('.tiptap-mathematics-render')) {
            return true;
        }

        const nestedTableRoot = target.closest('[data-content-type="table"]');
        if (nestedTableRoot && cellElement.contains(nestedTableRoot)) {
            return true;
        }

        const record = mutation as MutationRecord;

        if (record.type === 'childList' && isDecorationWidgetMutation(record)) {
            return true;
        }

        if (record.type === 'attributes' && record.target === cellElement) {
            return true;
        }

        return false;
    };
}

/**
 * Creates a tableheader or tablecell node view with ignoreMutation function
 * to prevent specific decoration widget mutations from causing re-renders of the node.
 *
 * Extracted shared logic between tableheader and tablecell node views.
 *
 * @param tag The tag name of the table header or cell.
 * @returns A table header or cell node view object with the following properties:
 * - dom: The DOM element representing the table header or cell.
 * - contentDOM: The DOM element representing the content of the table header or cell.
 * - ignoreMutation: A function that returns true if the mutation should be ignored.
 */
export function createTableCellNodeView(tag: 'td' | 'th') {
    return ({ node, HTMLAttributes, extension }: NodeViewRendererProps) => {
        const dom = document.createElement(tag);
        applyTableCellDomAttributes(dom, node, HTMLAttributes, extension.options.HTMLAttributes);

        return {
            dom,
            contentDOM: dom,
            ignoreMutation: tableCellIgnoreMutation(dom),
        };
    };
}
